/**
 * @typedef {object} WeeklyResponseBucket
 * @property {string} name - ISO-Monday label, `dd-mm`.
 * @property {number} value - Number of records that fall in that week.
 */

/**
 * Group records by ISO week (Monday-start) for the answers-per-week bar chart
 * shown inside chart info popovers. Records without a usable Firestore
 * `Timestamp` are skipped.
 *
 * @param {Array<{ Timestamp?: { toDate?: () => Date } }>} records
 * @returns {Array<WeeklyResponseBucket>} Buckets sorted oldest-first.
 */
const buildWeeklyResponseBuckets = (records) => {
    if (!Array.isArray(records) || records.length === 0) return [];

    /** @type {Map<number, { date: Date, count: number }>} */
    const buckets = new Map();

    records.forEach((record) => {
        const ts = record && record.Timestamp;
        if (!ts || typeof ts.toDate !== 'function') return;

        const d = ts.toDate();
        const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const weekdayDiff = (monday.getDay() + 6) % 7; // 0=Mon..6=Sun
        monday.setDate(monday.getDate() - weekdayDiff);

        const key = monday.getTime();
        const existing = buckets.get(key);
        if (existing) {
            existing.count += 1;
        } else {
            buckets.set(key, { date: monday, count: 1 });
        }
    });

    return Array.from(buckets.values())
        .sort((a, b) => a.date.getTime() - b.date.getTime())
        .map(({ date, count }) => ({
            name: `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}`,
            value: count,
        }));
};

export default buildWeeklyResponseBuckets;
